"use client";

import { useEffect } from "react";
import Link from "next/link";
import { PinContainer } from "@/components/ui/3d-pin";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchServices } from "@/store/slices/landingSlice";

export default function ServicesGrid() {
  const dispatch = useAppDispatch();
  const { data: services, loading, error } = useAppSelector((state) => state.landing.services);
  const { mode } = useAppSelector((state) => state.theme);

  useEffect(() => {
    if (services.length === 0) dispatch(fetchServices());
  }, [dispatch, services.length]);

  if (loading && services.length === 0) {
    return <div className="py-24 text-center text-gray-500 dark:text-gray-400">Loading services...</div>;
  }

  if (error && services.length === 0) {
    return <div className="py-24 text-center text-gray-500 dark:text-gray-400">Services are temporarily unavailable.</div>;
  }

  if (services.length === 0) {
    return <div className="py-24 text-center text-gray-500 dark:text-gray-400">No services available yet.</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-24 pt-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8">
      {services.map((service, index) => (
        <div
          key={service._id || service.id || service.slug || index}
          className="relative isolate z-0 flex h-[24rem] w-full items-center justify-center"
        >
          <PinContainer
            title="View service"
            mode={mode}
            href={`/services/${service.slug || service._id || service.id || ""}`}
            containerClassName="h-full w-full"
            className="h-[24rem] w-[20rem]"
          >
            <Link href={`/services/${service.slug || service._id || service.id || ""}`} className="group block h-full w-full">
              <article className={`flex h-full w-full flex-col overflow-hidden rounded-lg border transition-all duration-300 group-hover:border-[#8B31FF]/30 group-hover:shadow-xl ${mode === "dark" ? "border-white/10 bg-[#0D1117] group-hover:border-[#8B31FF]/40 group-hover:shadow-[0_0_30px_rgba(79,17,194,0.12)]" : "border-gray-100 bg-white"}`}>
                <div className="h-1 w-full bg-[#8B31FF]" />
                <div className="flex flex-1 flex-col p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="text-3xl">{service.icon || "⚡"}</span>
                  </div>
                  <h2 className={`mb-3 text-lg font-bold leading-snug transition-colors duration-200 group-hover:text-[#8B31FF] ${mode === "dark" ? "text-white dark:group-hover:text-[#A78BFA]" : "text-gray-900"}`}>
                    {service.title}
                  </h2>
                  <p className={`mb-5 flex-1 text-sm leading-relaxed ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                    {service.description}
                  </p>
                  <div className={`mt-auto flex items-center justify-between border-t pt-4 ${mode === "dark" ? "border-white/5" : "border-gray-100"}`}>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#8B31FF] transition-all duration-200 group-hover:gap-2 dark:text-[#A78BFA]">
                      Learn more
                      <span aria-hidden="true">→</span>
                    </span>
                  </div>
                </div>
              </article>
            </Link>
          </PinContainer>
        </div>
      ))}
    </div>
  );
}
